import { useShow, useUpdate, useOne } from '@refinedev/core'
import {
  Show,
  ShowButton,
  TextFieldComponent as TextField,
} from '@refinedev/mui'
import {
  Typography,
  Stack,
  Switch,
  Box,
  Button,
  Tab,
  Tabs,
  Chip,
  OutlinedInput,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useState, useEffect } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import OrgUsersManagement from '../../components/ManageOrgUsers'
import OrgChildrenManagement from '../../components/ManageOrgChildren'
import OrgAccountManagement from '../../components/ManageOrgAccounts'
import OrgProfile from '../../components/OrgProfile'
import BillingHistory from '../../components/BillingHistory'
import ActivityFeedSingleOrg, {
  ActivityWithLastLogin,
} from '../../components/ActivityFeedSingleOrg'
import OrgMemoriesPanel from '../../components/OrgMemoriesPanel'
import OrgOnboardingPanel from '../../components/OrgOnboardingPanel'
import OrgAppliedDiscountPanel from '../../components/OrgAppliedDiscountPanel'
import OrgEmbeddingRolloutPanel from '../../components/OrgEmbeddingRolloutPanel'

interface OrgsShowProps {
  tab?: string
}

export const OrgsShow = ({ tab }: OrgsShowProps) => {
  const { id } = useParams()
  const { query } = useShow({
    resource: 'orgs',
    id,
    errorNotification: (error) => {
      return {
        message: 'Error getting organisation details',
        description: error?.message || 'An error occurred while fetching org',
        type: 'error',
      }
    },
  })
  const { data, isLoading } = query

  const navigate = useNavigate()
  const location = useLocation()

  const record = data?.data

  // Tab management with URL routing
  const tabMap = {
    details: 0,
    billing: 1,
    'billing-history': 2,
    funding: 3,
    activity: 4,
    memories: 5,
    onboarding: 6,
  }
  const tabPaths = {
    0: 'details',
    1: 'billing',
    2: 'billing-history',
    3: 'funding',
    4: 'activity',
    5: 'memories',
    6: 'onboarding',
  }

  const [tabValue, setTabValue] = useState(() => {
    if (tab && tabMap[tab as keyof typeof tabMap] !== undefined) {
      return tabMap[tab as keyof typeof tabMap]
    }
    return 0 // default to details tab
  })

  const [earlyAccess, setEarlyAccess] = useState(record?.early_access)
  const [editingUsers, setEditingUsers] = useState(false)
  const [editingChildren, setEditingChildren] = useState(false)
  const [editingMaxTotalScans, setEditingMaxTotalScans] = useState(false)
  const [editingMaxTotalApplications, setEditingMaxTotalApplications] =
    useState(false)
  const [editingMaxTotalIntroLetters, setEditingMaxTotalIntroLetters] =
    useState(false)
  const [
    editingMaxTotalFundingDiscoveryRuns,
    setEditingMaxTotalFundingDiscoveryRuns,
  ] = useState(false)
  const [maxTotalScans, setMaxTotalScans] = useState(0)
  const [maxTotalApplications, setMaxTotalApplications] = useState(0)
  const [maxTotalIntroLetters, setMaxTotalIntroLetters] = useState(0)
  const [maxTotalFundingDiscoveryRuns, setMaxTotalFundingDiscoveryRuns] =
    useState(0)
  const { mutate: update } = useUpdate()

  // Fetch discount information for this specific organisation
  const { data: orgDiscountData, refetch: refetchOrgDiscount } = useOne({
    resource: 'discount-codes/orgs',
    id: `${record?.uuid}/discount`,
    queryOptions: {
      enabled: !!record?.uuid,
    },
  })

  // The discount data for this organisation
  const orgWithDiscount = orgDiscountData?.data

  const { data: activityData, isLoading: activityDataLoading } = useOne({
    resource: 'org-activity',
    id: record?.uuid,
    queryOptions: {
      enabled: !!record?.uuid,
    },
  })

  // Update tab when prop changes
  useEffect(() => {
    if (tab && tabMap[tab as keyof typeof tabMap] !== undefined) {
      setTabValue(tabMap[tab as keyof typeof tabMap])
    }
  }, [tab])
  useEffect(() => {
    setMaxTotalScans(record?.max_total_scans)
    setMaxTotalApplications(record?.max_total_applications)
    setMaxTotalIntroLetters(record?.max_total_intro_letters)
    setMaxTotalFundingDiscoveryRuns(record?.max_total_funding_discovery_runs)
  }, [record])

  function a11yProps(index: number) {
    return {
      id: `org-tab-${index}`,
      'aria-controls': `org-tabpanel-${index}`,
    }
  }

  function handleTabChange(e: React.SyntheticEvent, newValue: number) {
    setTabValue(newValue)

    // Navigate to appropriate URL
    const orgId = record?.uuid
    if (orgId) {
      const tabPath = tabPaths[newValue as keyof typeof tabPaths]
      const newPath = `/orgs/${orgId}/${tabPath}`

      if (location.pathname !== newPath) {
        navigate(newPath)
      }
    }
  }

  const handleEarlyAccessChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.checked
    setEarlyAccess(newValue)
    update({
      resource: 'orgs',
      id: record?.uuid,
      values: { early_access: newValue },
      errorNotification: (error) => {
        return {
          message: 'Error updating org',
          description: error?.message || 'An error occurred while updating org',
          type: 'error',
        }
      },
    })
  }

  function handleHasSubOrgsUpdate(event: React.ChangeEvent<HTMLInputElement>) {
    const newValue = event.target.checked
    update({
      resource: 'orgs',
      id: record?.uuid,
      values: { has_sub_orgs: newValue },
      errorNotification: (error) => {
        return {
          message: 'Error updating org',
          description:
            error?.response?.data?.detail == 'Child orgs cannot have sub orgs'
              ? 'Child orgs cannot have sub orgs'
              : error?.response?.data?.detail ==
                'You must update or delete all child orgs before removing the ability to have sub orgs'
              ? 'You must update or delete all child orgs before removing the ability to have sub orgs'
              : 'An error occurred while updating org',
          type: 'error',
        }
      },
    })
  }

  function handlePauseResumeEnabledUpdate(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    const newValue = event.target.checked
    update(
      {
        resource: 'orgs',
        id: record?.uuid,
        values: { pause_resume_enabled: newValue },
        successNotification: () => {
          return {
            message: 'Updated pause/resume feature flag',
            description: newValue
              ? 'Pause/resume functionality enabled for this organisation'
              : 'Pause/resume functionality disabled for this organisation',
            type: 'success',
          }
        },
        errorNotification: (error) => {
          return {
            message: 'Error updating org',
            description:
              error?.message || 'An error occurred while updating org',
            type: 'error',
          }
        },
      },
      {
        onSuccess: () => {
          // Force refetch the org data to update the UI
          query.refetch()
        },
      }
    )
  }

  function handleUpdateMaxTotalScans() {
    if (
      record?.membership &&
      record?.membership == 'Free' &&
      maxTotalScans < 0
    ) {
      const confirm = window.confirm(
        'Are you sure you want to set Unlimited scans for this organisation? They are currently on a free plan.'
      )
      if (!confirm) {
        return
      }
    }
    update({
      resource: 'orgs',
      id: record?.uuid,
      values: { max_total_scans: maxTotalScans },
      errorNotification: (error) => {
        return {
          message: 'Error updating org',
          description: 'An error occurred while updating org max total scans',
          type: 'error',
        }
      },
      successNotification: () => {
        setEditingMaxTotalScans(false)
        return {
          message: 'Updated max total scans',
          description: 'Successfully updated max total scans',
          type: 'success',
        }
      },
    })
  }

  function handleUpdateQuota(
    field:
      | 'max_total_applications'
      | 'max_total_intro_letters'
      | 'max_total_funding_discovery_runs',
    value: number,
    setEditing: (value: boolean) => void,
    label: string,
  ) {
    update({
      resource: 'orgs',
      id: record?.uuid,
      values: { [field]: value },
      errorNotification: () => {
        return {
          message: 'Error updating org',
          description: `An error occurred while updating ${label}`,
          type: 'error',
        }
      },
      successNotification: () => {
        setEditing(false)
        return {
          message: `Updated ${label}`,
          description: `Successfully updated ${label}`,
          type: 'success',
        }
      },
    })
  }

  if (data)
    return (
      <Show
        isLoading={isLoading}
        goBack={
          <IconButton onClick={() => navigate('/orgs')}>
            <ArrowBackIcon />
          </IconButton>
        }
        title={
          <Typography variant="h4" component="h1" fontWeight={600}>
            {record?.name || 'Organisation'}
          </Typography>
        }
      >
        <Box sx={{ borderBotton: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Org Details" {...a11yProps(0)} />
            <Tab label="Billing" {...a11yProps(1)} />
            <Tab label="Billing History" {...a11yProps(2)} />
            <Tab label="Funding" {...a11yProps(3)} />
            <Tab label="Activity" {...a11yProps(4)} />
            <Tab label="Memories" {...a11yProps(5)} />
            <Tab label="Onboarding" {...a11yProps(6)} />
          </Tabs>
        </Box>
        {tabValue == 0 && (
          <Stack gap={1}>
            <Typography variant="body1" fontWeight="bold">
              Name
            </Typography>
            <TextField value={record?.name} />
            <Typography variant="body1" fontWeight="bold">
              Charities Number
            </Typography>
            <TextField value={record?.charities_number} />
            <Typography variant="body1" fontWeight="bold">
              Accepts Gambling Proceeds:{' '}
              <span style={{ fontWeight: 'normal' }}>
                {record?.accepts_gambling_proceeds ? 'Yes' : 'No'}
              </span>
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              Accepts International Funding:{' '}
              <span style={{ fontWeight: 'normal' }}>
                {record?.accepts_international ? 'Yes' : 'No'}
              </span>
            </Typography>

            {/* Discount Information */}
            {orgWithDiscount &&
              orgWithDiscount.discount_code &&
              orgWithDiscount.discount_amount &&
              orgWithDiscount.discount_amount > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'success.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'success.200',
                  }}
                >
                  <Typography variant="h6" color="success.main" gutterBottom>
                    Active Discount
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Discount Code
                      </Typography>
                      <Chip
                        label={orgWithDiscount.discount_code}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Discount Amount
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        color="success.main"
                      >
                        -$
                        {orgWithDiscount.discount_amount?.toFixed(2) || '0.00'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Expires
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {orgWithDiscount.expires_at
                          ? format(
                              new Date(orgWithDiscount.expires_at),
                              'MMM d, yyyy'
                            )
                          : 'No expiry date'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

            <Typography variant="body1" fontWeight="bold">
              Active
            </Typography>
            <Typography>
              {record?.active ? (
                <CheckCircleIcon color="success" fontSize="inherit" />
              ) : (
                <CancelIcon color="error" fontSize="inherit" />
              )}
            </Typography>

            <Typography variant="body1" fontWeight="bold">
              Profile Update Status
            </Typography>
            <Typography
              color={
                record?.profile_regen_status === 'failed'
                  ? record?.profile_regen_failure_reason === 'insufficient_data'
                    ? 'warning.main'
                    : 'error.main'
                  : record?.profile_regen_status === 'in_progress' ||
                      record?.profile_regen_status === 'pending'
                    ? 'warning.main'
                    : 'text.secondary'
              }
            >
              {record?.profile_regen_status === 'in_progress' ||
              record?.profile_regen_status === 'pending'
                ? 'Updating — a background regeneration is in progress'
                : record?.profile_regen_status === 'failed'
                  ? record?.profile_regen_failure_reason === 'insufficient_data'
                    ? 'Needs more info — waiting on the user to answer profile questions'
                    : 'Last update failed — the user can retry from their profile'
                  : 'Up to date'}
            </Typography>

            <Typography variant="body1" fontWeight="bold">
              Early Access{' '}
              {record?.early_access ? (
                <CheckCircleIcon color="success" fontSize="inherit" />
              ) : (
                <CancelIcon color="error" fontSize="inherit" />
              )}
            </Typography>
            <Switch
              checked={record?.early_access}
              onChange={handleEarlyAccessChange}
              key={'early_access'}
            />
            <Typography variant="body1" fontWeight="bold">
              Has Sub Orgs
            </Typography>
            <Switch
              checked={record?.has_sub_orgs}
              onChange={handleHasSubOrgsUpdate}
              key={'has_sub_orgs'}
            />
            <Typography variant="body1" fontWeight="bold">
              Pause/Resume Enabled{' '}
              {record?.pause_resume_enabled ? (
                <CheckCircleIcon color="success" fontSize="inherit" />
              ) : (
                <CancelIcon color="error" fontSize="inherit" />
              )}
              {record?.payment_type === null && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="span"
                  sx={{ ml: 1 }}
                >
                  (sub-orgs cannot pause)
                </Typography>
              )}
            </Typography>
            <Switch
              checked={record?.pause_resume_enabled || false}
              onChange={handlePauseResumeEnabledUpdate}
              disabled={record?.payment_type === null}
              key={'pause_resume_enabled'}
            />
            {/* Pause/Resume reminders for admin - only for annual/quarterly invoice subscriptions */}
            {(record?.billing_frequency === 'Yearly' ||
              record?.billing_frequency === 'Quarterly') &&
              record?.payment_type === 'Invoice' && (
                <Box sx={{ gridColumn: 'span 2', mb: 1 }}>
                  <Typography
                    variant="caption"
                    color="warning.main"
                    display="block"
                  >
                    ⚠️ This org has{' '}
                    {record?.billing_frequency === 'Yearly'
                      ? 'an Annual'
                      : 'a Quarterly'}{' '}
                    invoice subscription
                  </Typography>
                </Box>
              )}
            <Typography variant="body1" fontWeight="bold">
              Status
            </Typography>
            <TextField
              value={record?.status ? record.status : 'No status available'}
            />
            <Typography variant="body1" fontWeight="bold">
              Max Total Scans:{' '}
              {record?.max_total_scans == -1
                ? 'Unlimited'
                : record?.max_total_scans}
              {record?.membership !== 'Paid' && (
                <EditOutlinedIcon
                  sx={{
                    cursor: 'pointer',
                    ml: 1,
                    color: 'primary.main',
                    fontSize: '20px',
                  }}
                  onClick={() => setEditingMaxTotalScans(true)}
                />
              )}
            </Typography>

            <Typography variant="body1" mb={2}>
              Used{' '}
              {record?.total_scans == 1
                ? '1 scan'
                : record?.total_scans + ' scans'}{' '}
              {record?.max_total_scans !== -1
                ? `of ${record?.max_total_scans}`
                : ''}
            </Typography>
            {editingMaxTotalScans == true && record?.membership !== 'Paid' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '250px',
                  gap: 0.5,
                  mb: 2,
                  p: 1,
                  border: '1px solid #ccc',
                  alignItems: 'center',
                }}
              >
                <FormControlLabel
                  label="Unlimited"
                  labelPlacement="end"
                  control={
                    <Checkbox
                      checked={maxTotalScans == -1}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setMaxTotalScans(-1)
                        } else {
                          setMaxTotalScans(2)
                        }
                      }}
                    />
                  }
                />

                {maxTotalScans !== -1 && (
                  <>
                    <Typography
                      variant="body1"
                      sx={{ my: 0.5, fontWeight: 'bold' }}
                    >
                      OR
                    </Typography>
                    <Typography variant="body1">
                      Set a Max Number of Scans
                    </Typography>
                    <OutlinedInput
                      type="number"
                      value={maxTotalScans}
                      size="small"
                      onChange={(e) => setMaxTotalScans(Number(e.target.value))}
                    />
                  </>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-evenly',
                    my: 1,
                    width: '100%',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setEditingMaxTotalScans(false)}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="contained"
                    onClick={() => handleUpdateMaxTotalScans()}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            )}

            <Typography variant="body1" fontWeight="bold">
              Max Total Applications:{' '}
              {record?.max_total_applications == -1
                ? 'Unlimited'
                : record?.max_total_applications}
              {record?.membership !== 'Paid' && (
                <EditOutlinedIcon
                  sx={{
                    cursor: 'pointer',
                    ml: 1,
                    color: 'primary.main',
                    fontSize: '20px',
                  }}
                  onClick={() => setEditingMaxTotalApplications(true)}
                />
              )}
            </Typography>
            {editingMaxTotalApplications && record?.membership !== 'Paid' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '250px',
                  gap: 0.5,
                  mb: 2,
                  p: 1,
                  border: '1px solid #ccc',
                  alignItems: 'center',
                }}
              >
                <FormControlLabel
                  label="Unlimited"
                  labelPlacement="end"
                  control={
                    <Checkbox
                      checked={maxTotalApplications == -1}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setMaxTotalApplications(-1)
                        } else {
                          setMaxTotalApplications(1)
                        }
                      }}
                    />
                  }
                />
                {maxTotalApplications !== -1 && (
                  <OutlinedInput
                    value={maxTotalApplications}
                    type="number"
                    onChange={(e) =>
                      setMaxTotalApplications(Number(e.target.value))
                    }
                    sx={{ maxWidth: 120 }}
                  />
                )}
                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    handleUpdateQuota(
                      'max_total_applications',
                      maxTotalApplications,
                      setEditingMaxTotalApplications,
                      'max total applications',
                    )
                  }
                >
                  Save
                </Button>
              </Box>
            )}

            <Typography variant="body1" fontWeight="bold">
              Max Total Intro Letters:{' '}
              {record?.max_total_intro_letters == -1
                ? 'Unlimited'
                : record?.max_total_intro_letters}
              {record?.membership !== 'Paid' && (
                <EditOutlinedIcon
                  sx={{
                    cursor: 'pointer',
                    ml: 1,
                    color: 'primary.main',
                    fontSize: '20px',
                  }}
                  onClick={() => setEditingMaxTotalIntroLetters(true)}
                />
              )}
            </Typography>
            {editingMaxTotalIntroLetters && record?.membership !== 'Paid' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '250px',
                  gap: 0.5,
                  mb: 2,
                  p: 1,
                  border: '1px solid #ccc',
                  alignItems: 'center',
                }}
              >
                <FormControlLabel
                  label="Unlimited"
                  labelPlacement="end"
                  control={
                    <Checkbox
                      checked={maxTotalIntroLetters == -1}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setMaxTotalIntroLetters(-1)
                        } else {
                          setMaxTotalIntroLetters(1)
                        }
                      }}
                    />
                  }
                />
                {maxTotalIntroLetters !== -1 && (
                  <OutlinedInput
                    value={maxTotalIntroLetters}
                    type="number"
                    onChange={(e) =>
                      setMaxTotalIntroLetters(Number(e.target.value))
                    }
                    sx={{ maxWidth: 120 }}
                  />
                )}
                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    handleUpdateQuota(
                      'max_total_intro_letters',
                      maxTotalIntroLetters,
                      setEditingMaxTotalIntroLetters,
                      'max total intro letters',
                    )
                  }
                >
                  Save
                </Button>
              </Box>
            )}

            <Typography variant="body1" fontWeight="bold">
              Max Total Funding Discovery Runs:{' '}
              {record?.max_total_funding_discovery_runs == -1
                ? 'Unlimited'
                : record?.max_total_funding_discovery_runs}
              {record?.membership !== 'Paid' && (
                <EditOutlinedIcon
                  sx={{
                    cursor: 'pointer',
                    ml: 1,
                    color: 'primary.main',
                    fontSize: '20px',
                  }}
                  onClick={() => setEditingMaxTotalFundingDiscoveryRuns(true)}
                />
              )}
            </Typography>
            {editingMaxTotalFundingDiscoveryRuns &&
              record?.membership !== 'Paid' && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '250px',
                    gap: 0.5,
                    mb: 2,
                    p: 1,
                    border: '1px solid #ccc',
                    alignItems: 'center',
                  }}
                >
                  <FormControlLabel
                    label="Unlimited"
                    labelPlacement="end"
                    control={
                      <Checkbox
                        checked={maxTotalFundingDiscoveryRuns == -1}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMaxTotalFundingDiscoveryRuns(-1)
                          } else {
                            setMaxTotalFundingDiscoveryRuns(1)
                          }
                        }}
                      />
                    }
                  />
                  {maxTotalFundingDiscoveryRuns !== -1 && (
                    <OutlinedInput
                      value={maxTotalFundingDiscoveryRuns}
                      type="number"
                      onChange={(e) =>
                        setMaxTotalFundingDiscoveryRuns(Number(e.target.value))
                      }
                      sx={{ maxWidth: 120 }}
                    />
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() =>
                      handleUpdateQuota(
                        'max_total_funding_discovery_runs',
                        maxTotalFundingDiscoveryRuns,
                        setEditingMaxTotalFundingDiscoveryRuns,
                        'max total funding discovery runs',
                      )
                    }
                  >
                    Save
                  </Button>
                </Box>
              )}

            {data?.data.has_sub_orgs ? (
              <Box display="flex" width={'100%'} justifyContent="space-between">
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Organisation's Child Orgs
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => setEditingChildren(!editingChildren)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {editingChildren ? 'Close' : `Manage Child Orgs`}
                </Button>
              </Box>
            ) : data?.data.has_sub_orgs === false &&
              data?.data.is_child_of_name ? (
              <>
                <Typography variant="body1" fontWeight="bold">
                  Organisation's Parent Org
                </Typography>

                <Typography variant="body1">
                  {data?.data.is_child_of_name}
                  <ShowButton
                    hideText
                    recordItemId={data?.data.is_child_of_uuid}
                  />
                </Typography>
              </>
            ) : null}
            {data?.data.uuid && editingChildren && data?.data.has_sub_orgs && (
              <Box bgcolor={'#f5f5f5'} borderRadius={4} p={2}>
                <Typography variant="h5" gutterBottom>
                  Manage Child Orgs
                </Typography>
                {data?.data.has_sub_orgs && (
                  <OrgChildrenManagement orgUuid={data?.data.uuid} />
                )}
              </Box>
            )}
            <Box display="flex" width={'100%'} justifyContent="space-between">
              <Box>
                <Typography variant="body1" fontWeight="bold">
                  Organisation Users
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => setEditingUsers(!editingUsers)}
                sx={{ alignSelf: 'flex-start' }}
              >
                {editingUsers ? 'Close' : "Manage Organisation's Users"}
              </Button>
            </Box>
            {data?.data.uuid && editingUsers && (
              <Box bgcolor={'#f5f5f5'} borderRadius={4} p={2}>
                <Typography variant="h5" gutterBottom>
                  Manage Users for {data?.data.name}
                </Typography>
                <OrgUsersManagement orgUuid={data?.data.uuid} />
              </Box>
            )}
            {data?.data.uuid && (
              <OrgEmbeddingRolloutPanel
                orgUuid={data.data.uuid}
                localEmbeddingsEnabled={data.data.local_embeddings_enabled}
                onChanged={() => query.refetch()}
              />
            )}
          </Stack>
        )}
        {tabValue == 1 && (
          <>
            {isLoading ? (
              <Typography>Loading billing information...</Typography>
            ) : data?.data?.uuid ? (
              <>
                <OrgAppliedDiscountPanel
                  orgUuid={data.data.uuid}
                  discount={orgWithDiscount as any}
                  onChanged={() => {
                    refetchOrgDiscount()
                    query.refetch()
                  }}
                />
                <OrgAccountManagement
                  orgUuid={data.data.uuid}
                  parentOrgName={data.data.is_child_of_name}
                  hasSubOrgs={data.data.has_sub_orgs}
                />
              </>
            ) : (
              <Typography>
                Unable to load billing information - organisation data not
                found.
              </Typography>
            )}
          </>
        )}
        {tabValue == 2 && (
          <>
            {isLoading ? (
              <Typography>Loading billing history...</Typography>
            ) : data?.data?.uuid ? (
              <BillingHistory orgUuid={data.data.uuid} />
            ) : (
              <Typography>
                Unable to load billing history - organisation data not found.
              </Typography>
            )}
          </>
        )}
        {tabValue == 3 && (
          <>
            {isLoading ? (
              <Typography>Loading funding information...</Typography>
            ) : data?.data?.uuid ? (
              <OrgProfile
                orgUuid={data.data.uuid}
                orgData={data.data}
                isFetchingOrg={isLoading}
              />
            ) : (
              <Typography>
                Unable to load funding information - organisation data not
                found.
              </Typography>
            )}
          </>
        )}
        {tabValue == 4 && (
          <ActivityFeedSingleOrg
            activityData={activityData?.data as ActivityWithLastLogin}
            isLoading={activityDataLoading}
            lastLoginDate={activityData?.data?.last_login_date}
            lastLoginUserName={activityData?.data?.last_login_user_name}
          />
        )}
        {tabValue == 5 &&
          (data?.data?.uuid ? (
            <OrgMemoriesPanel orgUuid={data.data.uuid} />
          ) : (
            <Typography>
              Unable to load memories - organisation data not found.
            </Typography>
          ))}
        {tabValue == 6 &&
          (data?.data?.uuid ? (
            <OrgOnboardingPanel orgUuid={data.data.uuid} />
          ) : (
            <Typography>
              Unable to load onboarding progress - organisation data not found.
            </Typography>
          ))}
      </Show>
    )
}
